import LayoutTabs from "components/LayoutTabs";
import Loading from "components/Loading";
import React, { lazy, Suspense } from "react";
import { Navigate, RouteObject, useRoutes } from "react-router-dom";
import { routePath } from "./path";

interface IRoute extends RouteObject {
  children?: IRoute[];
  [key: string]: any;
}

const Component: React.FC<{ component: any }> = ({ component: Component }) => (
  <Component />
);

const routes: IRoute[] = [
  // Private routes
  {
    path: routePath.Home,
    element: <Component component={lazy(() => import("./PrivateRoute"))} />,
    children: [
      {
        path: routePath.Home,
        element: (
          <Component component={lazy(() => import("pages/projects-page"))} />
        ),
      },
      {
        path: routePath.DocumentTaskList,
        element: (
          <Component
            component={lazy(() => import("pages/task/document-task-list"))}
          />
        ),
      },
      {
        path: routePath.Profile,
        element: <Component component={lazy(() => import("pages/profile"))} />,
      },
      {
        path: routePath.ForgeViewer,
        element: (
          <Component component={lazy(() => import("pages/forge-viewer"))} />
        ),
      },
      {
        path: routePath.DocumentLevel,
        element: (
          <Component
            component={lazy(() => import("pages/document/document-level-page"))}
          />
        ),
        isCheckAuth: true,
      },
      {
        path: routePath.DocumentTemplate,
        element: (
          <Component
            component={lazy(() => import("pages/document/template-page"))}
          />
        ),
      },
      {
        path: routePath.DocumentTemplateEdit,
        element: (
          <Component
            component={lazy(() => import("pages/document/template-page"))}
          />
        ),
      },
      {
        path: routePath.DocumentTemplateView,
        element: (
          <Component
            component={lazy(() => import("pages/document/template-page"))}
          />
        ),
        props: {
          isOnlyView: true,
        },
      },
      {
        path: routePath.blackboardTemplate,
        element: (
          <Component
            component={lazy(() => import("pages/document/blackboard-template"))}
          />
        ),
        isCheckAuth: true,
        props: {
          isBlackboardTemplate: true,
        },
      },

      // tasksheet template
      {
        path: routePath.TaskSheetTemplateList,
        element: (
          <Component
            component={lazy(
              () => import("pages/document/tasksheet-template/list")
            )}
          />
        ),
      },
      {
        path: routePath.TaskSheetTemplateEdit,
        element: (
          <Component
            component={lazy(() => import("pages/document/tasksheet-template"))}
          />
        ),
      },
      {
        path: routePath.TaskSheetTemplate,
        element: (
          <Component
            component={lazy(() => import("pages/document/tasksheet-template"))}
          />
        ),
      },
      {
        path: routePath.TaskSheetTemplateView,
        element: (
          <Component
            component={lazy(() => import("pages/document/tasksheet-template"))}
          />
        ),
        props: {
          isOnlyView: true,
        },
      },
      {
        path: routePath.SettingFamily,
        element: (
          <Component component={lazy(() => import("pages/setting-family"))} />
        ),
      },
      {
        path: routePath.ProjectOverview,
        element: (
          <Component component={lazy(() => import("pages/project-overview"))} />
        ),
      },
      {
        path: routePath.Home,
        element: <Component component={LayoutTabs} />,
      },
      {
        path: routePath.ListTaskType,
        element: (
          <Component component={lazy(() => import("pages/list-task-type"))} />
        ),
      },
      {
        path: routePath.InspectionList,
        element: (
          <Component component={lazy(() => import("pages/inspection/list"))} />
        ),
      },
      {
        path: routePath.InspectionEditSetting,
        element: (
          <Component
            component={lazy(() => import("pages/inspection/setting"))}
          />
        ),
      },
      {
        path: routePath.InspectionDetail,
        element: (
          <Component
            component={lazy(() => import("pages/inspection/detail"))}
          />
        ),
      },
      {
        path: routePath.InspectionLayout,
        element: (
          <Component
            component={lazy(() => import("pages/inspection/format"))}
          />
        ),
      },
      {
        path: routePath.InspectionLayoutList,
        element: (
          <Component
            component={lazy(() => import("pages/inspection/format-list"))}
          />
        ),
      },
      {
        path: routePath.Shorten,
        element: <Component component={lazy(() => import("./ShortenRoute"))} />,
        privateRouteProps: {
          callbackUrl: window.location.pathname + window.location.search,
        },
      },
      {
        path: "*",
        element: (
          <Component component={lazy(() => import("pages/NotFoundPage"))} />
        ),
      },
    ],
  },

  // Public routes
  {
    path: routePath.AccessError,
    element: (
      <Component component={lazy(() => import("pages/AccessErrorPage"))} />
    ),
  },
  {
    path: routePath.NotFound,
    element: <Component component={lazy(() => import("pages/NotFoundPage"))} />,
  },
  {
    path: "*",
    element: <Component component={lazy(() => import("pages/NotFoundPage"))} />,
  },
];

function AppRoute() {
  const element = useRoutes(routes);

  return <Suspense fallback={<Loading />}>{element}</Suspense>;
}

export default AppRoute;
